<template>
  <div class="space-y-6">

    <!-- Create form trigger -->
    <div v-if="!showCreateForm">
      <button
        id="akm-create-btn"
        class="inline-flex items-center gap-2 rounded-2xl border border-dashed border-border bg-muted/20 hover:bg-muted/40 hover:border-indigo-500/50 px-5 py-3 text-[11px] font-black uppercase tracking-widest text-muted-foreground hover:text-indigo-400 transition-all duration-200"
        @click="showCreateForm = true"
      >
        <Plus class="w-4 h-4" />
        Nouvelle clé API
      </button>
    </div>

    <!-- Create form -->
    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 -translate-y-2"
      leave-active-class="transition-all duration-150 ease-in"
      leave-to-class="opacity-0 -translate-y-2"
    >
      <div v-if="showCreateForm" class="rounded-2xl border border-indigo-500/30 bg-indigo-950/20 p-6 space-y-5">
        <div class="flex items-center justify-between">
          <h3 class="text-[11px] font-black uppercase tracking-widest text-foreground">Nouvelle clé API</h3>
          <button class="text-muted-foreground hover:text-foreground transition-colors" @click="cancelCreate" aria-label="Annuler">
            <X class="w-4 h-4" />
          </button>
        </div>

        <!-- Name -->
        <div class="space-y-2">
          <label class="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1" for="akm-name">
            Nom de la clé
          </label>
          <input
            id="akm-name"
            v-model="form.name"
            type="text"
            placeholder="ex: Mon agent GPT"
            class="w-full bg-muted/30 border border-border rounded-2xl p-4 font-bold focus:ring-4 focus:ring-primary/10 outline-none transition-all text-foreground placeholder:text-muted-foreground/40 text-sm"
            @keydown.enter="create"
          />
        </div>

        <!-- Scopes -->
        <div class="space-y-2">
          <label class="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Permissions (scopes)</label>
          <div class="space-y-2">
            <label
              v-for="scope in availableScopes"
              :key="scope.key"
              class="flex items-start gap-3 rounded-xl border border-border bg-muted/20 hover:bg-muted/40 p-4 cursor-pointer transition-all"
              :class="{ 'border-indigo-500/50 bg-indigo-950/30': form.scopes.includes(scope.key) }"
            >
              <input
                type="checkbox"
                :value="scope.key"
                v-model="form.scopes"
                class="mt-0.5 accent-indigo-500"
              />
              <div>
                <span class="block text-xs font-black font-mono text-indigo-400">{{ scope.key }}</span>
                <span class="block text-[11px] text-muted-foreground mt-0.5">{{ scope.description }}</span>
              </div>
            </label>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex justify-end gap-3 pt-2">
          <button
            class="rounded-xl border border-border bg-transparent px-4 py-2 text-[11px] font-black uppercase tracking-widest text-muted-foreground hover:bg-muted/40 transition-all"
            @click="cancelCreate"
          >
            Annuler
          </button>
          <button
            class="inline-flex items-center gap-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 px-5 py-2 text-[11px] font-black uppercase tracking-widest text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-900/30"
            :disabled="creating || !form.name.trim() || form.scopes.length === 0"
            @click="create"
          >
            <RefreshCw v-if="creating" class="w-3.5 h-3.5 animate-spin" />
            <Key v-else class="w-3.5 h-3.5" />
            Générer la clé
          </button>
        </div>
      </div>
    </Transition>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center gap-3 py-8 text-muted-foreground">
      <RefreshCw class="w-4 h-4 animate-spin" />
      <span class="text-xs font-bold uppercase tracking-widest">Chargement…</span>
    </div>

    <!-- Empty state -->
    <div v-else-if="keys.length === 0 && !showCreateForm" class="flex flex-col items-center justify-center py-16 space-y-4 text-center">
      <div class="w-14 h-14 rounded-2xl border border-border bg-muted/20 flex items-center justify-center">
        <KeyRound class="w-6 h-6 text-muted-foreground" />
      </div>
      <div class="space-y-1">
        <p class="text-sm font-black text-foreground">Aucune clé active</p>
        <p class="text-[11px] text-muted-foreground italic">Créez une clé pour permettre à un agent IA d'accéder à vos données en lecture seule.</p>
      </div>
    </div>

    <!-- Keys list -->
    <div v-else-if="keys.length > 0" class="space-y-2">
      <p class="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-3">{{ keys.length }} clé{{ keys.length > 1 ? 's' : '' }} active{{ keys.length > 1 ? 's' : '' }}</p>
      <div
        v-for="key in keys"
        :key="key.id"
        class="rounded-xl border border-border bg-muted/10 hover:bg-muted/20 px-4 py-3 transition-all flex items-center gap-3"
      >
        <!-- Icon -->
        <div class="shrink-0 w-8 h-8 rounded-lg bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center">
          <Key class="w-3.5 h-3.5 text-indigo-400" />
        </div>

        <!-- Content -->
        <div class="flex-1 min-w-0 space-y-1">
          <div class="flex items-center gap-2 flex-wrap">
            <span class="font-black text-foreground text-sm truncate">{{ key.name }}</span>
            <span class="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 text-[10px] font-bold text-emerald-400 uppercase tracking-widest shrink-0">
              <span class="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block"></span>
              Active
            </span>
          </div>
          <div class="flex flex-wrap items-center gap-1.5">
            <span
              v-for="scope in key.scopes"
              :key="scope"
              class="font-mono text-[10px] rounded-md bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 px-1.5 py-0.5"
            >
              {{ scope }}
            </span>
            <span class="text-[10px] text-muted-foreground">·</span>
            <span class="text-[10px] text-muted-foreground">{{ formatDate(key.createdAt) }}</span>
            <span v-if="key.lastUsedAt" class="text-[10px] text-emerald-400/70">· utilisée {{ formatDate(key.lastUsedAt) }}</span>
            <span v-else class="text-[10px] text-muted-foreground italic">· jamais utilisée</span>
          </div>
        </div>

        <!-- Revoke button — always visible -->
        <button
          class="shrink-0 rounded-lg border border-red-500/20 bg-red-500/5 hover:bg-red-500/20 hover:border-red-500/40 p-2 text-red-400 hover:text-red-300 transition-all"
          :aria-label="`Révoquer la clé ${key.name}`"
          :title="`Révoquer «\u00a0${key.name}\u00a0»`"
          @click="confirmRevoke(key)"
        >
          <Trash2 class="w-3.5 h-3.5" />
        </button>
      </div>
    </div>

    <!-- Error -->
    <p v-if="error" class="rounded-xl bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-400 font-bold" role="alert">
      {{ error }}
    </p>

    <!-- Raw Key Modal -->
    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0"
      leave-active-class="transition-all duration-150 ease-in"
      leave-to-class="opacity-0"
    >
      <div
        v-if="rawKeyModal"
        class="fixed inset-0 z-[3000] flex items-center justify-center p-4 bg-zinc-950/70 backdrop-blur-sm"
        @mousedown.self="rawKeyModal = null"
      >
        <div class="w-full max-w-md rounded-2xl border border-indigo-500/30 bg-zinc-900 shadow-2xl p-8 space-y-5 animate-in zoom-in-95 duration-200">
          <div class="text-center space-y-2">
            <div class="w-12 h-12 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center mx-auto">
              <KeyRound class="w-5 h-5 text-indigo-400" />
            </div>
            <h3 class="text-base font-black text-foreground uppercase tracking-widest">Clé générée !</h3>
          </div>

          <div class="rounded-xl bg-amber-500/10 border border-amber-500/20 px-4 py-3 flex items-start gap-2">
            <AlertTriangle class="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
            <p class="text-xs text-amber-300 font-bold leading-relaxed">
              Cette clé ne sera <strong>plus jamais affichée</strong>. Copiez-la maintenant et conservez-la en lieu sûr.
            </p>
          </div>

          <!-- Key display -->
          <div class="rounded-xl border border-border bg-zinc-950 p-4 flex items-center gap-3">
            <code class="flex-1 font-mono text-xs text-violet-400 break-all leading-relaxed">{{ rawKeyModal }}</code>
            <button
              class="shrink-0 rounded-lg border transition-all px-3 py-2 text-xs font-black"
              :class="copied
                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400'
                : 'border-border bg-muted/30 text-muted-foreground hover:text-foreground hover:bg-muted/50'"
              :aria-label="copied ? 'Copié !' : 'Copier la clé'"
              @click="copyKey"
            >
              <Check v-if="copied" class="w-4 h-4" />
              <Copy v-else class="w-4 h-4" />
            </button>
          </div>

          <button
            class="w-full rounded-2xl bg-indigo-600 hover:bg-indigo-500 px-4 py-3 text-[11px] font-black uppercase tracking-widest text-white transition-all shadow-lg shadow-indigo-900/30"
            @click="rawKeyModal = null"
          >
            J'ai copié ma clé — Fermer
          </button>
        </div>
      </div>
    </Transition>

    <!-- Revoke confirm modal -->
    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0"
      leave-active-class="transition-all duration-150 ease-in"
      leave-to-class="opacity-0"
    >
      <div
        v-if="revokeTarget"
        class="fixed inset-0 z-[3000] flex items-center justify-center p-4 bg-zinc-950/70 backdrop-blur-sm"
        @mousedown.self="revokeTarget = null"
        role="dialog"
        aria-modal="true"
      >
        <div class="w-full max-w-sm rounded-2xl border border-red-500/30 bg-zinc-900 shadow-2xl p-8 space-y-5 animate-in zoom-in-95 duration-200">
          <div class="text-center space-y-2">
            <div class="w-12 h-12 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto">
              <Trash2 class="w-5 h-5 text-red-400" />
            </div>
            <h3 class="text-base font-black text-foreground">Révoquer cette clé ?</h3>
            <p class="text-sm text-muted-foreground">
              La clé <span class="font-bold text-foreground">« {{ revokeTarget.name }} »</span> sera immédiatement invalidée. Les intégrations qui l'utilisent cesseront de fonctionner.
            </p>
          </div>
          <div class="flex gap-3">
            <button
              class="flex-1 rounded-xl border border-border bg-transparent px-4 py-3 text-[11px] font-black uppercase tracking-widest text-muted-foreground hover:bg-muted/40 transition-all"
              @click="revokeTarget = null"
            >
              Annuler
            </button>
            <button
              class="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 hover:bg-red-500 px-4 py-3 text-[11px] font-black uppercase tracking-widest text-white transition-all disabled:opacity-50"
              :disabled="revoking"
              @click="revoke"
            >
              <RefreshCw v-if="revoking" class="w-3.5 h-3.5 animate-spin" />
              <Trash2 v-else class="w-3.5 h-3.5" />
              Révoquer
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useGlobalState } from '../../../composables/useGlobalState.js'
import { Plus, X, Key, KeyRound, RefreshCw, Check, Copy, Trash2, AlertTriangle } from 'lucide-vue-next'

interface ApiKeyMeta {
  id: number
  name: string
  scopes: string[]
  lastUsedAt: string | null
  createdAt: string
}

interface ScopeInfo {
  key: string
  description: string
}

const { getAuthHeader } = useGlobalState()

const keys = ref<ApiKeyMeta[]>([])
const availableScopes = ref<ScopeInfo[]>([])
const loading = ref(true)
const error = ref<string | null>(null)
const showCreateForm = ref(false)
const creating = ref(false)
const revoking = ref(false)
const revokeTarget = ref<ApiKeyMeta | null>(null)
const rawKeyModal = ref<string | null>(null)
const copied = ref(false)

const form = ref({ name: '', scopes: [] as string[] })

async function fetchKeys() {
  loading.value = true
  error.value = null
  try {
    const res = await fetch('/api/public/finance/api-keys', { headers: getAuthHeader() })
    if (!res.ok) throw new Error('Erreur lors du chargement des clés.')
    const data = await res.json()
    keys.value = data.keys
    availableScopes.value = data.availableScopes
  } catch (e: any) {
    error.value = e.message
  } finally {
    loading.value = false
  }
}

async function create() {
  if (!form.value.name.trim() || form.value.scopes.length === 0) return
  creating.value = true
  error.value = null
  try {
    const res = await fetch('/api/public/finance/api-keys', {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: form.value.name, scopes: form.value.scopes }),
    })
    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.error ?? 'Erreur lors de la création.')
    }
    const data = await res.json()
    rawKeyModal.value = data.rawKey
    keys.value.unshift(data.key)
    cancelCreate()
  } catch (e: any) {
    error.value = e.message
  } finally {
    creating.value = false
  }
}

function cancelCreate() {
  showCreateForm.value = false
  form.value = { name: '', scopes: [] }
}

function confirmRevoke(key: ApiKeyMeta) {
  revokeTarget.value = key
}

async function revoke() {
  if (!revokeTarget.value) return
  revoking.value = true
  error.value = null
  try {
    const res = await fetch(`/api/public/finance/api-keys/${revokeTarget.value.id}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    })
    if (!res.ok) throw new Error('Erreur lors de la révocation.')
    keys.value = keys.value.filter(k => k.id !== revokeTarget.value!.id)
    revokeTarget.value = null
  } catch (e: any) {
    error.value = e.message
  } finally {
    revoking.value = false
  }
}

async function copyKey() {
  if (!rawKeyModal.value) return
  try {
    // Preferred: Clipboard API (requires HTTPS or localhost)
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(rawKeyModal.value)
    } else {
      // Fallback: execCommand (works on HTTP)
      const textarea = document.createElement('textarea')
      textarea.value = rawKeyModal.value
      textarea.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0'
      document.body.appendChild(textarea)
      textarea.focus()
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    }
    copied.value = true
    setTimeout(() => { copied.value = false }, 2000)
  } catch (e) {
    error.value = 'Impossible de copier automatiquement. Sélectionnez la clé manuellement.'
  }
}

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso))
}

onMounted(fetchKeys)
</script>
